import { useState } from 'react';

import CreateReportModal from 'dashboard/components/CreateReportModal';
import { FormCreateReportData } from 'dashboard/forms/FormCreateReport';
import { Account, FieldsMapLegacyUser } from 'dashboard/models/account';
import { AccountType } from 'dashboard/models/account-type';
import { ReportTemplateFilterOption } from 'dashboard/models/report-template';
import { CountryCode } from 'lib/constants/locales';
import { FieldString } from 'lib/fields';
import { ApiModel } from 'lib/interfaces/api';
import PiiAttributes from 'lib/interfaces/PiiAttributes';
import { Iso8601DateString } from 'lib/types';
import { assertNever } from 'lib/utils/types';

const EXCLUDED_REPORT_TYPES = [
  ApiModel.ReportTemplateBusinessAdverseMedia,
  ApiModel.ReportTemplateBusinessAssociatedPersons,
  ApiModel.ReportTemplateBusinessClassification,
  ApiModel.ReportTemplateBusinessIndustryClassification,
  ApiModel.ReportTemplateBusinessLiensLookup,
  ApiModel.ReportTemplateBusinessLookup,
  ApiModel.ReportTemplateBusinessOnlinePresence,
  ApiModel.ReportTemplateBusinessRegistrationsLookup,
  ApiModel.ReportTemplateBusinessWatchlist,
  ApiModel.ReportTemplateBusinessWebsite,
  // TODO: [Jason] - Implement this sometime
  ApiModel.ReportTemplateScreening,
];

interface AccountCreateReportModalProps {
  accountType: AccountType;
  reportTemplateFilterOptions: ReportTemplateFilterOption[];
  handleCreateReport: (reportTemplateFilterOption: ReportTemplateFilterOption) => void;
  handleClose: () => void;
  account: Account;
}

const AccountCreateReportModal = ({
  accountType,
  reportTemplateFilterOptions,
  handleCreateReport,
  handleClose,
  account,
}: AccountCreateReportModalProps) => {
  const [selectedReportTemplateFilterOption, setSelectedReportTemplateFilterOption] =
    useState<ReportTemplateFilterOption>();

  const filteredReportTemplateFilterOptions = reportTemplateFilterOptions.filter(
    (reportTemplateFilterOption) =>
      !EXCLUDED_REPORT_TYPES.includes(reportTemplateFilterOption.type),
  );

  const handleReportTemplateFilterOptionSelect = (reportTemplateId?: string) => {
    setSelectedReportTemplateFilterOption(
      reportTemplateFilterOptions.find((rt) => rt.id === reportTemplateId),
    );
  };

  const onCreateReport = async (
    _data: FormCreateReportData,
    reportTemplateFilterOption: ReportTemplateFilterOption,
  ) => {
    handleClose();
    return handleCreateReport(reportTemplateFilterOption);
  };

  // we can't prefill from non-legacy accounts (we don't know what the field schemas map to)
  const prefillData = accountType.attributes['is-legacy-account-type']
    ? getPrefillFromAccount(account, selectedReportTemplateFilterOption)
    : {};

  return (
    <CreateReportModal
      reportTemplateFilterOptions={filteredReportTemplateFilterOptions}
      selectedReportTemplateFilterOption={selectedReportTemplateFilterOption}
      handleClose={() => {
        handleReportTemplateFilterOptionSelect(undefined);
        handleClose();
      }}
      handleCreateReport={onCreateReport}
      handleReportTemplateFilterOptionSelect={handleReportTemplateFilterOptionSelect}
      showCsvUploadTab={false}
      hideReferenceId={true}
      initialValues={prefillData}
      readOnly={true}
    />
  );
};

export default AccountCreateReportModal;

const getPrefillFromAccount = (
  account: Account,
  reportTemplateFilterOption: ReportTemplateFilterOption | undefined,
): Partial<FormCreateReportData> => {
  const legacyUserFields = account.attributes.fields as unknown as FieldsMapLegacyUser;
  const ssnField = legacyUserFields.identification_numbers.value?.find(
    (p) => p.value.identification_class.value === 'ssn',
  );

  // Before a user selects a reportTemplate, reportTemplate will be null.
  if (reportTemplateFilterOption == null || account == null) return {};

  const fieldName = account.attributes.fields['name']?.value as Record<string, FieldString>;
  const fieldAddress = account.attributes.fields['address']?.value as Record<string, FieldString>;

  const nameFirst = fieldName?.first?.value;
  const nameMiddle = fieldName?.middle?.value;
  const nameLast = fieldName?.last?.value;
  const addressStreet1 = fieldAddress?.street_1?.value;
  const addressStreet2 = fieldAddress?.street_2?.value;
  const addressCity = fieldAddress?.city?.value;
  const addressSubdivision = fieldAddress?.subdivision?.value;
  const addressPostalCode = fieldAddress?.postal_code?.value;
  const addressCountryCode = fieldAddress?.country_code?.value;
  const birthdate = account.attributes.fields.birthdate?.value as Iso8601DateString;
  const phoneNumber = account.attributes.fields.phone_number?.value as string;
  const emailAddress = account.attributes.fields.email_address?.value as string;

  switch (reportTemplateFilterOption.type) {
    case ApiModel.ReportTemplateAddressLookup:
      return {
        [PiiAttributes.Address]: {
          street1: addressStreet1,
          street2: addressStreet2,
          city: addressCity,
          subdivision: addressSubdivision,
          postalCode: addressPostalCode,
        },
      };
    case ApiModel.ReportTemplateAdverseMedia:
      return {
        [PiiAttributes.NameFirst]: nameFirst,
        [PiiAttributes.NameMiddle]: nameMiddle,
        [PiiAttributes.NameLast]: nameLast,
        [PiiAttributes.Birthdate]: birthdate,
        [PiiAttributes.CountryCode]: addressCountryCode as CountryCode,
      };
    case ApiModel.ReportTemplateEmailAddress:
      return {
        [PiiAttributes.EmailAddress]: emailAddress,
      };
    case ApiModel.ReportTemplateEquifaxOneview:
      return {
        [PiiAttributes.NameFirst]: nameFirst,
        [PiiAttributes.NameMiddle]: nameMiddle,
        [PiiAttributes.NameLast]: nameLast,
        [PiiAttributes.SocialSecurityNumber]: ssnField?.value?.identification_number?.value,
      };
    case ApiModel.ReportTemplateFinraBrokerCheck:
      return {
        [PiiAttributes.NameFull]: account.attributes['name-full'],
        [PiiAttributes.AddressCity]: addressCity,
        [PiiAttributes.AddressSubdivision]: addressSubdivision,
      };
    case ApiModel.ReportTemplatePhoneNumber:
      return {
        [PiiAttributes.PhoneNumber]: phoneNumber,
      };
    case ApiModel.ReportTemplateProfileNonAuthoritative:
      return {
        [PiiAttributes.NameFirst]: nameFirst,
        [PiiAttributes.NameMiddle]: nameMiddle,
        [PiiAttributes.NameLast]: nameLast,
        [PiiAttributes.AddressSubdivision]: addressSubdivision,
        [PiiAttributes.AddressPostalCode]: addressPostalCode,
        [PiiAttributes.AddressCity]: addressCity,
        [PiiAttributes.EmailAddress]: emailAddress,
        [PiiAttributes.PhoneNumber]: phoneNumber,
      };
    case ApiModel.ReportTemplateSecActionLookup:
      return {
        [PiiAttributes.NameFirst]: nameFirst,
        [PiiAttributes.NameLast]: nameLast,
        [PiiAttributes.Birthdate]: birthdate,
      };
    case ApiModel.ReportTemplateSentilinkScores:
      return {
        [PiiAttributes.Address]: {
          street1: addressStreet1,
          street2: addressStreet2,
          city: addressCity,
          subdivision: addressSubdivision,
          postalCode: addressPostalCode,
        },
        [PiiAttributes.Birthdate]: birthdate,
        [PiiAttributes.CountryCode]: addressCountryCode as CountryCode,
        [PiiAttributes.NameFirst]: nameFirst,
        [PiiAttributes.NameLast]: nameLast,
        [PiiAttributes.SocialSecurityNumber]: ssnField?.value?.identification_number?.value,
      };
    case ApiModel.ReportTemplateSocialMedia:
      return {
        [PiiAttributes.NameFirst]: nameFirst,
        [PiiAttributes.NameMiddle]: nameMiddle,
        [PiiAttributes.NameLast]: nameLast,
        [PiiAttributes.AddressSubdivision]: addressSubdivision,
        [PiiAttributes.AddressPostalCode]: addressPostalCode,
        [PiiAttributes.AddressCity]: addressCity,
        [PiiAttributes.EmailAddress]: emailAddress,
        [PiiAttributes.PhoneNumber]: phoneNumber,
      };
    case ApiModel.ReportTemplateSynthetic:
      return {
        [PiiAttributes.Address]: {
          street1: addressStreet1,
          street2: addressStreet2,
          city: addressCity,
          subdivision: addressSubdivision,
          postalCode: addressPostalCode,
        },
        [PiiAttributes.Birthdate]: birthdate,
        [PiiAttributes.CountryCode]: addressCountryCode as CountryCode,
        [PiiAttributes.NameFirst]: nameFirst,
        [PiiAttributes.NameLast]: nameLast,
        [PiiAttributes.SocialSecurityNumber]: ssnField?.value?.identification_number?.value,
      };
    case ApiModel.ReportTemplateWatchlist:
      return {
        [PiiAttributes.NameFirst]: nameFirst,
        [PiiAttributes.NameMiddle]: nameMiddle,
        [PiiAttributes.NameLast]: nameLast,
        [PiiAttributes.Birthdate]: birthdate,
        [PiiAttributes.CountryCode]: addressCountryCode as CountryCode,
      };
    case ApiModel.ReportTemplatePoliticallyExposedPerson:
      return {
        [PiiAttributes.NameFirst]: nameFirst,
        [PiiAttributes.NameMiddle]: nameMiddle,
        [PiiAttributes.NameLast]: nameLast,
        [PiiAttributes.Birthdate]: birthdate,
        [PiiAttributes.CountryCode]: addressCountryCode as CountryCode,
      };
    case ApiModel.ReportTemplateProfile:
      return {
        [PiiAttributes.NameFirst]: nameFirst,
        [PiiAttributes.NameLast]: nameLast,
        [PiiAttributes.Birthdate]: birthdate,
        [PiiAttributes.Address]: {
          street1: addressStreet1,
          street2: addressStreet2,
          city: addressCity,
          subdivision: addressSubdivision,
          postalCode: addressPostalCode,
        },
        [PiiAttributes.CountryCode]: addressCountryCode as CountryCode,
        [PiiAttributes.SocialSecurityNumber]: ssnField?.value?.identification_number?.value,
        [PiiAttributes.PhoneNumber]: phoneNumber,
      };
    // Report template types that are unsupported for ad-hoc creation
    case ApiModel.ReportTemplateBusinessAdverseMedia:
    case ApiModel.ReportTemplateBusinessAssociatedPersons:
    case ApiModel.ReportTemplateBusinessClassification:
    case ApiModel.ReportTemplateBusinessIndustryClassification:
    case ApiModel.ReportTemplateBusinessLiensLookup:
    case ApiModel.ReportTemplateBusinessLookup:
    case ApiModel.ReportTemplateBusinessNonprofit:
    case ApiModel.ReportTemplateBusinessOnlinePresence:
    case ApiModel.ReportTemplateBusinessRegistrationsLookup:
    case ApiModel.ReportTemplateBusinessSocialMedia:
    case ApiModel.ReportTemplateBusinessWatchlist:
    case ApiModel.ReportTemplateBusinessWebsite:
    case ApiModel.ReportTemplateChainalysisAddressScreening:
    case ApiModel.ReportTemplateClearbitBusinessLookup:
    case ApiModel.ReportTemplateCoinbaseCheckCryptoRisk:
    case ApiModel.ReportTemplateCryptoAddressWatchlist:
    case ApiModel.ReportTemplateCustomList:
    case ApiModel.ReportTemplateKyckrBusinessLookup:
    case ApiModel.ReportTemplateMiddeskBusinessLookup:
    case ApiModel.ReportTemplateMxAccount:
    case ApiModel.ReportTemplateScreening:
    case ApiModel.ReportTemplateSentilinkApplicationRisk:
    case ApiModel.ReportTemplateTrmWalletScreening:
      return {};
    default:
      assertNever(reportTemplateFilterOption.type);
  }
};
